import { ChevronLeft, ChevronRight, ChevronsLeft, ChevronsRight, Ellipsis } from 'lucide-react';
import * as React from 'react';

import { Button, type ButtonProps } from '@/components/ui/button';
import { cn } from '@/lib/utils';

/**
 * Pagination (shadcn/ui) en boutons : la pagination du dashboard est locale
 * (aucun paramètre de page côté API). Boutons `btn-page` 28px, page courante
 * `aria-current="page"` (fond `accent`, contour `foreground`). Les pages
 * utilisent `Pager` (pager.tsx), qui compose ces éléments.
 */

function Pagination({ className, ...props }: React.ComponentProps<'nav'>): React.JSX.Element {
  return <nav aria-label="Pagination" className={cn('flex', className)} {...props} />;
}

const PaginationContent = React.forwardRef<HTMLUListElement, React.ComponentProps<'ul'>>(
  ({ className, ...props }, ref) => (
    <ul
      ref={ref}
      className={cn('m-0 flex list-none flex-row items-center gap-1 p-0', className)}
      {...props}
    />
  ),
);
PaginationContent.displayName = 'PaginationContent';

const PaginationItem = React.forwardRef<HTMLLIElement, React.ComponentProps<'li'>>(
  ({ className, ...props }, ref) => <li ref={ref} className={className} {...props} />,
);
PaginationItem.displayName = 'PaginationItem';

interface PaginationButtonProps extends Omit<ButtonProps, 'variant' | 'size'> {
  isActive?: boolean;
}

function PaginationButton({
  isActive = false,
  className,
  ...props
}: PaginationButtonProps): React.JSX.Element {
  return (
    <Button
      type="button"
      variant="outline"
      size="page"
      aria-current={isActive ? 'page' : undefined}
      className={className}
      {...props}
    />
  );
}

function PaginationFirst(props: PaginationButtonProps): React.JSX.Element {
  return (
    <PaginationButton aria-label="Première page" {...props}>
      <ChevronsLeft aria-hidden="true" />
    </PaginationButton>
  );
}

function PaginationPrevious(props: PaginationButtonProps): React.JSX.Element {
  return (
    <PaginationButton aria-label="Page précédente" {...props}>
      <ChevronLeft aria-hidden="true" />
    </PaginationButton>
  );
}

function PaginationNext(props: PaginationButtonProps): React.JSX.Element {
  return (
    <PaginationButton aria-label="Page suivante" {...props}>
      <ChevronRight aria-hidden="true" />
    </PaginationButton>
  );
}

function PaginationLast(props: PaginationButtonProps): React.JSX.Element {
  return (
    <PaginationButton aria-label="Dernière page" {...props}>
      <ChevronsRight aria-hidden="true" />
    </PaginationButton>
  );
}

function PaginationEllipsis({
  className,
  ...props
}: React.ComponentProps<'span'>): React.JSX.Element {
  return (
    <span
      aria-hidden="true"
      className={cn('flex size-7 items-center justify-center text-muted-foreground', className)}
      {...props}
    >
      <Ellipsis className="size-4" />
    </span>
  );
}

export {
  Pagination,
  PaginationContent,
  PaginationButton,
  PaginationItem,
  PaginationFirst,
  PaginationPrevious,
  PaginationNext,
  PaginationLast,
  PaginationEllipsis,
};
